import React, { useEffect, useState } from 'react';

import { ParsedQuery } from 'query-string';
import Loadable from 'react-loadable';
import { connect } from 'react-redux';
import { Redirect, Switch } from 'react-router';
import { Route } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { loadOrg } from './actions/load-org';
import { accountSelector } from '../account/account-selectors';
import { loadAccount } from '../account/actions/load-account';
import { userSelector } from '../reducers/current-user-selectors';
import { orgSelector, orgStatusSelector } from '../reducers/org-selectors';

import { AuthenticationRequiredRoute } from '../authentication/authentication-required-route';
import { OrgAutomations } from '../automations/org-automations';
import { EmployeeGroupsPage } from '../employee-groups/components/employee-groups-page';
import { EmployeeMessagesPage } from '../employee-messages/employee-messages-page';
import { EmployeeList } from '../employees/employee-list';
import { EmployeeProfile } from '../employees/profile/employee-profile';
import { EmployeeSurveys } from '../employees/surveys/employee-surveys';
import { EventCalendarPage } from '../event-calendar/event-calendar-page';
import { MultiOrgIntegrationConfig } from '../merge-integration/multi-org-integration-config/multi-org-integration-config';
import { RealTime } from '../real-time/real-time';
import { EmployeeReferrals } from '../referrals/employee-referrals';
import {
  employeeCommunicationRoute,
  employeeGroupsRoute,
  employeeMessagesRoute,
  employeeReferralsRoute,
  employeeRoute,
  employeesRoute,
  eventCalendarRoute,
  HOME_ROUTE,
  multiOrgIntegrationConfigRoute,
  orgAdminRoute,
  orgAutomationsRoute,
  orgJobsRoute,
  orgSettingsRoute,
  surveysRoute,
} from '../web-routes';

import { AccountStatusMessage } from './account-status-message';
import { OrganizationInactivePage } from './organization-inactive-page';
import { OrganizationNav } from './organization-nav';
import { OrganizationSettingsPage } from './organization-settings-page';

import { canViewEmployeeAutomations } from '../automations/utils/automation-permission-utils';
import { CalendarDisplayType } from '../event-calendar/utils/calendar-display-type';
import { EMPLOYEE_PERMISSION, HIRING_PERMISSION } from '../util/permissions';
import { LoadingMessage } from '../widgets/loading-message';
import { useUpdateLastVisitedOrg } from '../widgets/use-update-last-visited-org';

const AdminAccountPage = Loadable({
  loader: () => import('../admin/admin-panel/admin-account-page'),
  loading: LoadingMessage,
});

const JobListPage = Loadable({
  loader: () => import('../job/job-list'),
  loading: LoadingMessage,
});

type InputProps = {
  orgId: number;
  queryParams: ParsedQuery;
};

const mapStateToProps = (state: any, { orgId }: InputProps) => {
  const org = orgSelector(state, orgId);

  return {
    org,
    account: org ? accountSelector(state, org.accountId) : null,
    orgStatus: orgStatusSelector(state, orgId),
    user: userSelector(state),
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    loadOrg: (notFoundHandler: (args: { [arg: string]: any }) => void) =>
      dispatch(loadOrg(orgId, { notFoundHandler })),
    loadAccount: (accountId: number) => dispatch(loadAccount(accountId)),
  },
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

type Props = InputProps & StateProps & DispatchProps;

export const OrganizationRoutingPage = connect(
  mapStateToProps,
  mapDispatchToProps,
)(({ actions, user, orgId, org, account, orgStatus, queryParams }: Props) => {
  const [isNotFound, setIsNotFound] = useState<boolean>(false);
  useEffect(() => {
    actions.loadOrg(() => {
      setIsNotFound(true);
    });
    if (user.isAdmin) {
      RealTime.subscribeAdminToOrg(user, orgId);
    }
    if (org) {
      actions.loadAccount(org.accountId);
    }
  }, [user, orgId, org, actions]);

  const theme = useTheme();
  // The small screen breakpoint is used to switch flex direction of the page content
  // This must be the same breakpoint used to switch the nav from the left to the top of the page in UiIconNav
  const isOnSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isOnMobile = useMediaQuery(theme.breakpoints.down('xs'));

  useUpdateLastVisitedOrg({ orgId });

  const userIsEmployeeOnly =
    user.hiringPermission === HIRING_PERMISSION.NONE &&
    user.employeesPermission !== EMPLOYEE_PERMISSION.NONE;

  if (isNotFound) {
    return <Redirect to={HOME_ROUTE} />;
  }

  if (!org || orgStatus?.loading || !account) {
    return <LoadingMessage />;
  }

  if (!org) {
    return <Redirect to={HOME_ROUTE} />;
  }

  if (!org.isActive) {
    return <OrganizationInactivePage orgId={orgId} accountId={org.accountId} />;
  }

  return (
    <div
      className={`u-zIndex-1 u-overflow-hidden u-position-relative u-display-flex page-root-height ${
        isOnSmallScreen ? 'u-flexDirection-column' : ''
      }`}
    >
      <OrganizationNav org={org} />
      <div className="u-width-100 u-height-100 u-overflowY-hidden">
        <AccountStatusMessage accountId={org.accountId} />
        <div className="u-height-100 u-overflowX-hidden">
          <Switch>
            <Route
              path={surveysRoute(orgId)}
              render={({
                match: {
                  params: { surveyId },
                },
              }) => (
                <div className="u-padding-bottom-clearChat">
                  <EmployeeSurveys
                    orgId={orgId}
                    surveyId={surveyId ? parseInt(surveyId, 10) : null}
                  />
                </div>
              )}
            />
            <Route
              path={eventCalendarRoute(orgId)}
              render={() => (
                <div className="u-padding-bottom-clearChat">
                  <EventCalendarPage
                    orgId={orgId}
                    calendarDisplay={
                      isOnMobile ? CalendarDisplayType.PARTIAL_WEEK : CalendarDisplayType.MONTH
                    }
                  />
                </div>
              )}
            />
            <Route
              path={employeeReferralsRoute(orgId)}
              render={() => (
                <div className="u-padding-bottom-clearChat">
                  <EmployeeReferrals orgId={orgId} />
                </div>
              )}
            />
            <Route
              path={orgAutomationsRoute(orgId)}
              render={() => {
                const hasEmployeeAutomationViewPermission = canViewEmployeeAutomations(user);
                const tab = hasEmployeeAutomationViewPermission ? queryParams?.tab : null;

                return (
                  <div className="u-padding-bottom-clearChat">
                    <OrgAutomations orgId={orgId} tab={tab} queryParams={queryParams} />
                  </div>
                );
              }}
            />
            <Route
              path={employeeMessagesRoute({ orgId })}
              render={() => <EmployeeMessagesPage orgId={orgId} queryParams={queryParams} />}
            />
            <Route
              path={employeeGroupsRoute(orgId)}
              render={() => (
                <div className="u-padding-bottom-clearChat">
                  <EmployeeGroupsPage orgId={orgId} />
                </div>
              )}
            />
            <Route
              path={multiOrgIntegrationConfigRoute(orgId)}
              render={() => (
                <div className="u-padding-bottom-clearChat">
                  <MultiOrgIntegrationConfig originOrgId={orgId} />
                </div>
              )}
            />
            <Route
              path={employeeCommunicationRoute(orgId)}
              render={() => <Redirect to={employeeMessagesRoute({ orgId })} />}
            />
            <Route
              path={orgSettingsRoute(orgId)}
              render={() => (
                <div className="u-padding-bottom-clearChat">
                  <OrganizationSettingsPage orgId={org.org_id} />
                </div>
              )}
            />
            <Route
              path={employeeRoute()}
              render={(props) => {
                const tab =
                  queryParams.tab && !Array.isArray(queryParams.tab) ? queryParams.tab : null;
                return (
                  <div className="u-maxWidth-800px u-margin-0Auto u-padding-top-lg u-padding-bottom-clearChat">
                    <EmployeeProfile
                      employeeId={parseInt(props.match.params.employeeId, 10)}
                      tab={tab}
                    />
                  </div>
                );
              }}
            />
            <Route
              path={employeesRoute(orgId)}
              render={() => (
                <div className="u-padding-bottom-clearChat">
                  <EmployeeList orgId={orgId} queryParams={queryParams} />
                </div>
              )}
            />
            <AuthenticationRequiredRoute
              requiresAdmin
              path={orgAdminRoute(orgId)}
              render={() => (
                <AdminAccountPage
                  orgId={org.org_id}
                  accountId={org.accountId}
                  queryParams={queryParams}
                />
              )}
            />
            <Route
              path={orgJobsRoute()}
              render={() => <JobListPage orgId={org.org_id} orgKey={org.org_key} />}
            />
            <Route
              render={() => (
                <Redirect
                  to={userIsEmployeeOnly ? employeeMessagesRoute({ orgId }) : orgJobsRoute(orgId)}
                />
              )}
            />
          </Switch>
        </div>
      </div>
    </div>
  );
});
